import { HelmetProvider } from 'react-helmet-async'
import { QueryClientProvider } from 'react-query'
import { Outlet } from 'react-router'
import { IntercomProvider } from 'react-use-intercom'

import { CriticalUpdateNotification } from '@/components/layout/CriticalUpdateNotification.tsx'
import { LoadingScreen } from '@/components/layout/page/loading-screen/loading-screen.tsx'
import { ScrollToTop } from '@/components/layout/scroll-to-top.tsx'
import { BrowserProvider } from '@/components/provider/BrowserProvider/BrowserProvider.tsx'
import { queryClient } from '@/consts/query.ts'
import { isCUI } from '@/consts/routes.ts'
import { DialogsRoot } from '@/dialogs/DialogsRoot.tsx'
import { PageInfo } from '@/modules/HelpDesk/PageInfo.tsx'
import { DesktopAppProvider } from '@/modules/desktop/DesktopAppProvider.tsx'
import { PusherProvider } from '@/modules/pusher/PusherProvider.tsx'

import { DateRangeProvider } from './components/provider/date-range-provider.tsx'
import { ErrorBoundary } from './components/ui/ErrorBoundary/ErrorBoundary.tsx'
import { ToastViewport } from './components/ui/toast.tsx'
import { AccountProvider } from './modules/accounts/state/AccountProvider'
import { AccountGuard } from './modules/accounts/ui/AccountGuard'
import { LogoutHandler } from './modules/accounts/ui/LogoutHandler.tsx'
import { CampaignsProvider } from './modules/campaigns/state/provider.tsx'
import { CreatorProvider } from './modules/creators/state/CreatorProvider'
import { QuestionnaireRoot } from './modules/questionnaire/root.tsx'
import { SalesEditorProvider } from './modules/sales-editor/state/sales-editor.tsx'
import { INTERCOM_API_BASE, INTERCOM_APP_ID } from './utils/constants'
import { ThemeProvider } from './utils/useTheme'

export const App = () => {
  return (
    <ErrorBoundary
      boundaryName="App"
      fallbackTemplate="page"
    >
      <BrowserProvider>
        <QueryClientProvider client={queryClient}>
          <HelmetProvider>
            <IntercomProvider
              appId={INTERCOM_APP_ID}
              apiBase={INTERCOM_API_BASE}
            >
              <ToastViewport>
                <AccountProvider>
                  <DesktopAppProvider>
                    <ThemeProvider>
                      <LoadingScreen>
                        <AccountGuard>
                          <PusherProvider>
                            <CreatorProvider>
                              <DateRangeProvider>
                                <SalesEditorProvider>
                                  <CampaignsProvider>
                                    <LogoutHandler />
                                    <ScrollToTop />
                                    {!isCUI && <DialogsRoot />}
                                    <Outlet />
                                    <QuestionnaireRoot />
                                    <PageInfo />
                                    <CriticalUpdateNotification />
                                  </CampaignsProvider>
                                </SalesEditorProvider>
                              </DateRangeProvider>
                            </CreatorProvider>
                          </PusherProvider>
                        </AccountGuard>
                      </LoadingScreen>
                    </ThemeProvider>
                  </DesktopAppProvider>
                </AccountProvider>
              </ToastViewport>
            </IntercomProvider>
          </HelmetProvider>
        </QueryClientProvider>
      </BrowserProvider>
    </ErrorBoundary>
  )
}
